/**
 * App-version auto-update system.
 *
 * How it works:
 *   - Backend exposes /api/app-version returning { version: "x.y.z" }
 *   - On app boot AND on every successful login we call initAppVersionCheck()
 *   - First ever boot: we simply save the server version to localStorage
 *   - Subsequent boots: if server version != stored version → we're on stale
 *     code → save new version + forceAppUpdate() (clears caches & reloads)
 *   - No hardcoded frontend version string — fewer places to drift
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = 'app_version_seen';

async function fetchServerVersion() {
  try {
    const resp = await fetch(`${API_URL}/api/app-version`, { cache: 'no-store' });
    if (resp.ok) {
      const data = await resp.json();
      return data.version || null;
    }
  } catch {
    /* ignore; network hiccups shouldn't crash the app */
  }
  return null;
}

function getLastSeenVersion() {
  return localStorage.getItem(LS_KEY);
}

function setLastSeenVersion(v) {
  if (v) localStorage.setItem(LS_KEY, v);
}

/**
 * Current known app version (as last seen from the server). Used by the
 * Settings "Current Version" display. Falls back to 'unknown' before the
 * first /api/app-version round-trip completes.
 */
export function getAppVersion() {
  return getLastSeenVersion() || 'unknown';
}

/**
 * Compare current running version to server version.
 * Returns { updateAvailable, serverVersion, localVersion }.
 */
export async function checkServerVersion() {
  const serverVersion = await fetchServerVersion();
  if (!serverVersion) return { updateAvailable: false };
  const localVersion = getLastSeenVersion();
  if (localVersion && localVersion !== serverVersion) {
    return { updateAvailable: true, serverVersion, localVersion };
  }
  return { updateAvailable: false, serverVersion, localVersion };
}

/**
 * Silent auto-update check. Call this on app boot AND after login success.
 * - First boot ever: just saves server version (no reload, nothing to update yet)
 * - Subsequent boots with mismatch: saves new version + forces a reload with fresh caches
 * Returns true if an update was triggered (caller can await & not render anything).
 */
export async function initAppVersionCheck({ silent = true } = {}) {
  const serverVersion = await fetchServerVersion();
  if (!serverVersion) return false;
  const localVersion = getLastSeenVersion();

  // First ever boot — just remember what we saw, nothing to update
  if (!localVersion) {
    setLastSeenVersion(serverVersion);
    return false;
  }

  if (localVersion === serverVersion) return false;

  // Mismatch detected → save the new version FIRST (so the post-reload
  // boot check sees a match and doesn't trigger another reload), then force
  // update to swap caches.
  // eslint-disable-next-line no-console
  console.log(`[AppUpdate] Version drift detected: ${localVersion} -> ${serverVersion}. Updating...`);
  setLastSeenVersion(serverVersion);

  if (silent) {
    await forceAppUpdate();
  }
  return true;
}

/**
 * Force update the app by clearing all caches and reloading.
 * Preserves push-notification subscription & auth token.
 */
export async function forceAppUpdate() {
  // eslint-disable-next-line no-console
  console.log('[AppUpdate] Starting force update...');
  try {
    // 1. Ask active service worker to skip waiting / clear caches, but do NOT
    //    unregister it — we need it for push notifications.
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if (registration.waiting) {
        registration.waiting.postMessage('SKIP_WAITING');
      }
      if (registration.active) {
        registration.active.postMessage('CLEAR_CACHES');
      }
      await registration.update();
    }

    // 2. Nuke Cache API caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    // 3. Preserve auth token, user, push subscription and app_version_seen;
    //    drop everything else.
    const preserved = {
      token: localStorage.getItem('token'),
      user: localStorage.getItem('user'),
      pushSubscription: localStorage.getItem('pushSubscription'),
      [LS_KEY]: localStorage.getItem(LS_KEY),
    };
    localStorage.clear();
    for (const [k, v] of Object.entries(preserved)) {
      if (v) localStorage.setItem(k, v);
    }

    // 4. Hard reload — bypass every layer of cache
    window.location.reload(true);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[AppUpdate] forceAppUpdate failed:', e);
    // Best-effort fallback
    window.location.reload();
  }
}
